import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import api from './utils/api';
import Login from './pages/Login';
import POS from './pages/POS';
import Orders from './pages/Orders';
import EODReport from './pages/EODReport';
import Inventory from './pages/Inventory';
import ActivityLogPage from './pages/ActivityLogPage';
import Reports from './pages/Reports';
import Discounts from './pages/Discounts';
import ManageProducts from './pages/ManageProducts';
import AccessControl from './pages/AccessControl';
import Leads from './pages/Leads';
import Layout from './components/Layout';

function App() {
  const { isAuthenticated, user, touchActivity, logout } = useAuthStore();
  const roles = user?.roles || [];
  const isManager = roles.includes('manager') && !roles.includes('pos');
  const isSuperAdmin = roles.includes('super_admin');
  const showManagerArea = isManager || isSuperAdmin; // super admin sees the management tabs too

  // Idle timeout: track user activity
  useEffect(() => {
    if (!isAuthenticated) return;
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    let throttle = null;
    const handler = () => {
      if (throttle) return;
      throttle = setTimeout(() => { throttle = null; }, 30000); // throttle to every 30s
      touchActivity();
    };
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, handler));
  }, [isAuthenticated, touchActivity]);

  // Session revoked interceptor: if server returns sessionRevoked, force logout
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.data?.sessionRevoked) {
          logout();
          window.location.href = '/login?reason=revoked';
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(interceptor);
  }, [logout]);

  return (
    <Router>
      <Routes>
        <Route path="/login" element={
          isAuthenticated ? <Navigate to={showManagerArea ? "/eod-report" : "/"} /> : <Login />
        } />
        
        <Route path="/" element={
          isAuthenticated ? <Layout /> : <Navigate to="/login" />
        }>
          {showManagerArea ? (
            <>
              <Route index element={<Navigate to="/eod-report" />} />
              <Route path="orders" element={<Orders />} />
              <Route path="reports" element={<Reports />} />
              <Route path="discounts" element={<Discounts />} />
              <Route path="manage-products" element={<ManageProducts />} />
              {isSuperAdmin && <Route path="access-control" element={<AccessControl />} />}
              <Route path="eod-report" element={<EODReport />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="activity-log" element={<ActivityLogPage />} />
              <Route path="leads" element={<Leads />} />
              <Route path="leads/:id" element={<Leads />} />
            </>
          ) : (
            <>
              <Route index element={<POS />} />
              <Route path="orders" element={<Orders />} />
              <Route path="eod-report" element={<EODReport />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="activity-log" element={<ActivityLogPage />} />
              <Route path="leads" element={<Leads />} />
              <Route path="leads/:id" element={<Leads />} />
            </>
          )}
        </Route>

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}

export default App;
